function normalizeLaravelMountString(noTrailing: string): string {
  try {
    const u = new URL(noTrailing);
    if (/\/index\.php$/i.test(u.pathname)) {
      u.pathname = u.pathname.replace(/\/index\.php$/i, '') || '/';
    }
    return u.toString().replace(/\/$/, '');
  } catch {
    return noTrailing;
  }
}

/** Laravel application root URL, including a subfolder when used (e.g. https://staging.example.com/api). */
export function getLaravelMountUrl(): string {
  const raw = (process.env.NEXT_PUBLIC_LARAVEL_URL ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8000').trim();
  const noTrailing = raw.replace(/\/$/, '');
  return normalizeLaravelMountString(noTrailing);
}

/** @deprecated Prefer {@link getLaravelMountUrl}; kept for existing imports. */
export function getLaravelUrl(): string {
  return getLaravelMountUrl();
}

/**
 * Join a mount URL and an application-relative path without losing the mount subfolder.
 * Example: joinMountedUrl('https://staging.example.com/api', '/cart') -> https://staging.example.com/api/cart
 */
export function joinMountedUrl(baseUrl: string, path: string): string {
  const target = String(path || '').trim();
  if (!target) return baseUrl.replace(/\/$/, '');
  if (target.startsWith('http://') || target.startsWith('https://')) return target;

  const normalizedPath = target.startsWith('/') ? target : `/${target}`;
  try {
    const base = new URL(baseUrl);
    const mountPath = base.pathname.replace(/\/$/, '');
    const joinedPath = `${mountPath}${normalizedPath}`.replace(/\/{2,}/g, '/');
    return new URL(joinedPath, `${base.protocol}//${base.host}`).toString();
  } catch {
    return `${baseUrl.replace(/\/$/, '')}${normalizedPath}`;
  }
}

export function buildLaravelMountUrl(path: string): string {
  return joinMountedUrl(getLaravelMountUrl(), path);
}

/**
 * Public URL for Laravel-rendered pages that live on the site root (optional override).
 * Classified ad detail links use {@link buildLaravelMountUrl} (`/api/classified-ads/...`) like cart and forum.
 * Override with NEXT_PUBLIC_LARAVEL_PAGES_URL when routing differs.
 */
export function getLaravelPagesUrl(): string {
  const explicit = process.env.NEXT_PUBLIC_LARAVEL_PAGES_URL?.trim();
  if (explicit) {
    return normalizeLaravelMountString(explicit.replace(/\/$/, ''));
  }

  const mount = getLaravelMountUrl();
  const site = (process.env.NEXT_PUBLIC_SITE_URL ?? '').trim().replace(/\/$/, '');
  if (!site) return mount;

  try {
    const mountUrl = new URL(mount);
    const siteUrl = new URL(site.startsWith('http') ? site : `https://${site}`);
    if (
      mountUrl.hostname === siteUrl.hostname &&
      mountUrl.port === siteUrl.port &&
      /\/api$/i.test(mountUrl.pathname.replace(/\/$/, '') || '/api')
    ) {
      return siteUrl.origin;
    }
  } catch {
    /* use mount */
  }

  return mount;
}

export function buildLaravelPagesUrl(path: string): string {
  return joinMountedUrl(getLaravelPagesUrl(), path);
}

/**
 * Origin that serves `/storage/...` (Laravel `public/storage` symlink).
 * Set this when JSON is fetched from one host but uploaded files live on another (e.g. Next on
 * `buyme-next.*` and Laravel on `api-buyme.*`). If unset, {@link getLaravelMountUrl} is used — which
 * must not be the Next.js frontend URL or the navbar logo will 404 and fall back to `/static-header-logo.png`.
 */
export function getStoragePublicOrigin(): string {
  const explicit = process.env.NEXT_PUBLIC_STORAGE_ORIGIN?.trim();
  if (explicit) return explicit.replace(/\/$/, '');
  return getLaravelMountUrl();
}

/**
 * Absolute URL for media returned by Laravel (e.g. settings logo) when `asset()` produced a
 * relative path or APP_URL does not match the API host the browser should use.
 */
export function resolveSiteMediaUrl(href: string | undefined | null): string | undefined {
  const raw = href?.trim();
  if (!raw) return undefined;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('//')) {
    try {
      const u = new URL(getStoragePublicOrigin());
      return `${u.protocol}${raw}`;
    } catch {
      return `https:${raw}`;
    }
  }
  const base = raw.startsWith('/storage') ? getStoragePublicOrigin() : getLaravelMountUrl();
  if (raw.startsWith('/')) return `${base}${raw}`;
  return `${base}/${raw}`;
}

/** Favicon with the same fallback chain as Laravel `Setting::resolvedFaviconUrl()`. */
export function resolveFaviconUrl(
  favicon: string | undefined | null,
  logo?: string | undefined | null,
): string {
  const resolvedFavicon = resolveSiteMediaUrl(favicon);
  if (resolvedFavicon) return resolvedFavicon;
  const resolvedLogo = resolveSiteMediaUrl(logo);
  if (resolvedLogo) return resolvedLogo;
  return `${getLaravelMountUrl()}/default/logo.svg`;
}

/**
 * Base URL for credentialed requests when calling Laravel directly (legacy).
 * Prefer same-origin paths + Next {@link getNextProxyBase} for session-sensitive browser calls.
 */
export function getLaravelApiUrl(): string {
  const raw = (process.env.NEXT_PUBLIC_LARAVEL_API_URL ?? process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:8000').trim();
  const noTrailing = raw.replace(/\/$/, '');
  return normalizeLaravelMountString(noTrailing);
}

export function buildLaravelApiUrl(path: string): string {
  return joinMountedUrl(getLaravelApiUrl(), path);
}

/**
 * Laravel storefront links (cart, forum, classified ads, etc.) use the same mount as
 * {@link getLaravelMountUrl} — e.g. `https://buyme.lk/api` + `/forum` → `https://buyme.lk/api/forum`.
 */
export const laravelPaths = {
  cart: () => buildLaravelMountUrl('/cart'),
  login: (redirect?: string) => {
    const base = buildLaravelMountUrl('/login');
    return redirect ? `${base}?redirect=${encodeURIComponent(redirect)}` : base;
  },
  adminDashboard: () => buildLaravelMountUrl('/admin/dashboard'),
  sellerDashboard: () => buildLaravelMountUrl('/seller/dashboard'),
  dashboard: () => buildLaravelMountUrl('/account'),
  postAd: () => buildLaravelMountUrl('/account/classified-ads/create'),
  browse: () => buildLaravelMountUrl('/browse'),
  /** Forum requires `index.php` in the path on servers without front-controller rewrites. */
  forum: () => buildLaravelMountUrl('/index.php/forum'),
  verifiedStores: () => buildLaravelMountUrl('/verified-stores'),
  store: (slugOrId: string) => buildLaravelMountUrl(`/stores/${slugOrId}`),
  product: (slug: string) => buildLaravelMountUrl(`/products/${encodeURIComponent(slug)}`),
  classifiedAd: (slug: string) => buildLaravelMountUrl(`/classified-ads/${encodeURIComponent(slug)}`),
  category: (idOrSlug: string) => buildLaravelMountUrl(`/category/${encodeURIComponent(idOrSlug)}`),
  about: () => buildLaravelMountUrl('/about'),
  terms: () => buildLaravelMountUrl('/terms'),
  privacy: () => buildLaravelMountUrl('/privacy'),
  help: () => buildLaravelMountUrl('/help'),
  blogs: () => buildLaravelMountUrl('/blogs'),
  maintenance: () => buildLaravelMountUrl('/maintenance'),
};

/** Footer social icons — full URLs to brand pages. NEXT_PUBLIC_* is required for use in client components. */
export function getSocialUrls(): {
  facebook: string;
  twitter: string;
  instagram: string;
  linkedin: string;
} {
  return {
    facebook: (process.env.NEXT_PUBLIC_SOCIAL_FACEBOOK_URL ?? '').trim(),
    twitter: (process.env.NEXT_PUBLIC_SOCIAL_TWITTER_URL ?? '').trim(),
    instagram: (process.env.NEXT_PUBLIC_SOCIAL_INSTAGRAM_URL ?? '').trim(),
    linkedin: (process.env.NEXT_PUBLIC_SOCIAL_LINKEDIN_URL ?? '').trim(),
  };
}
